/**
 * Beat scheduler — schedules notes ahead of time for accurate timing.
 *
 * Uses the Web Audio API's precise scheduling to play notes on the beat grid.
 * Provides beat callbacks for visual synchronization.
 */

import type { AudioEngine } from './audio-engine';
import {
  playKick, playSnare, playHiHat, playBass, playLead, playPad, playArp, noteFreq,
} from './instruments';
import { getSong, type Song } from './songs';

/** Beat event for visual sync */
export interface BeatEvent {
  beat: number;       // Beat number within the bar (0-based)
  bar: number;        // Bar number (0-based)
  time: number;       // AudioContext time of the beat
  strength: number;   // 0-1, how strong this beat is (1 = downbeat)
}

export type BeatCallback = (event: BeatEvent) => void;

/** Music parameters from level settings */
export interface MusicParams {
  bpm: number;
  key: string;
  style: string;
  layers: string[];
  /** Song id (from src/audio/songs.ts). Falls back to the default if omitted. */
  song?: string;
}

/** A-minor scale notes by octave for pattern generation */
const AM_SCALE = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

function scaleNote(degree: number, octave: number): string {
  const note = AM_SCALE[((degree % 7) + 7) % 7];
  const oct = octave + Math.floor(degree / 7);
  return `${note}${oct}`;
}

export class BeatScheduler {
  private engine: AudioEngine;
  private params: MusicParams;
  private song: Song;

  // Timing
  private beatDuration = 0.5;       // seconds per beat (120 BPM default)
  private stepDuration = 0.125;     // seconds per 16th note
  private scheduleAhead = 0.15;     // schedule this far ahead
  private lookAhead = 25;           // check every N ms

  // State
  private currentStep = 0;          // Current 16th-note step
  private nextStepTime = 0;         // Audio time of next step
  private timerID: number | null = null;
  private playing = false;

  // Beat callbacks for visual sync
  private beatCallbacks: BeatCallback[] = [];

  constructor(engine: AudioEngine, params: MusicParams) {
    this.engine = engine;
    this.params = params;
    this.song = getSong(params.song);
    this.updateTiming();
  }

  private updateTiming(): void {
    this.beatDuration = 60 / this.params.bpm;
    this.stepDuration = this.beatDuration / 4; // 16th notes
  }

  /** Register a callback for beat events */
  onBeat(cb: BeatCallback): () => void {
    this.beatCallbacks.push(cb);
    return () => {
      this.beatCallbacks = this.beatCallbacks.filter((c) => c !== cb);
    };
  }

  /** Start playing music */
  start(): void {
    if (this.playing) return;
    const ctx = this.engine.context;
    if (!ctx) return;

    this.playing = true;
    this.currentStep = 0;
    this.nextStepTime = ctx.currentTime + 0.1;
    this.scheduler();
  }

  /** Stop playing */
  stop(): void {
    this.playing = false;
    if (this.timerID !== null) {
      clearTimeout(this.timerID);
      this.timerID = null;
    }
  }

  /** Change BPM mid-song */
  setBPM(bpm: number): void {
    this.params.bpm = bpm;
    this.updateTiming();
  }

  private scheduler(): void {
    if (!this.playing) return;
    const ctx = this.engine.context;
    if (!ctx) return;

    while (this.nextStepTime < ctx.currentTime + this.scheduleAhead) {
      this.scheduleStep(this.currentStep, this.nextStepTime);
      this.nextStepTime += this.stepDuration;
      this.currentStep++;
    }

    this.timerID = window.setTimeout(() => this.scheduler(), this.lookAhead);
  }

  private scheduleStep(step: number, time: number): void {
    const ctx = this.engine.context;
    if (!ctx) return;

    const stepsPerBar = 16;
    const barsPerLoop = 4;
    const totalSteps = stepsPerBar * barsPerLoop;

    const loopStep = step % totalSteps;
    const bar = Math.floor(loopStep / stepsPerBar);
    const barStep = loopStep % stepsPerBar;
    const beat = Math.floor(barStep / 4);

    // Fire beat callbacks on quarter notes
    if (barStep % 4 === 0) {
      const strength = barStep === 0 ? 1.0 : (barStep === 8 ? 0.7 : 0.4);
      const event: BeatEvent = {
        beat,
        bar: Math.floor(step / stepsPerBar),
        time,
        strength,
      };
      for (const cb of this.beatCallbacks) {
        // Schedule callback slightly before the beat for visual prep
        const delay = Math.max(0, (time - ctx.currentTime) * 1000);
        setTimeout(() => cb(event), delay);
      }
    }

    const layers = this.params.layers;

    // Drums
    if (layers.includes('drums')) {
      this.scheduleDrumStep(ctx, bar, barStep, time);
    }

    // Bass
    if (layers.includes('bass')) {
      this.scheduleBassStep(ctx, bar, barStep, time);
    }

    // Lead
    if (layers.includes('lead')) {
      this.scheduleLeadStep(ctx, bar, barStep, time);
    }

    // Pad (plays whole-bar chords)
    if (layers.includes('pad') && barStep === 0) {
      this.schedulePadBar(ctx, bar, time);
    }

    // Arp
    if (layers.includes('arp')) {
      this.scheduleArpStep(ctx, bar, barStep, time);
    }
  }

  private scheduleDrumStep(ctx: AudioContext, bar: number, step: number, time: number): void {
    const drums = this.song.drums;
    const pattern = drums[bar % drums.length];
    const ch = pattern[step % pattern.length];
    const output = this.engine.getLayer('drums').gain;

    switch (ch) {
      case 'K': playKick(ctx, output, time); break;
      case 'S': playSnare(ctx, output, time); break;
      case 'H': playHiHat(ctx, output, time, false); break;
      case 'O': playHiHat(ctx, output, time, true); break;
    }
  }

  private scheduleBassStep(ctx: AudioContext, bar: number, step: number, time: number): void {
    const bass = this.song.bass;
    const pattern = bass[bar % bass.length];
    const degree = pattern[step % pattern.length];
    if (degree === -1) return;

    const freq = noteFreq(scaleNote(degree, 2));
    const output = this.engine.getLayer('bass').gain;
    playBass(ctx, output, time, freq, this.stepDuration * 2);
  }

  private scheduleLeadStep(ctx: AudioContext, bar: number, step: number, time: number): void {
    const lead = this.song.lead;
    const pattern = lead[bar % lead.length];
    const degree = pattern[step % pattern.length];
    if (degree === -1) return;

    const freq = noteFreq(scaleNote(degree, 4));
    const output = this.engine.getLayer('lead').gain;
    playLead(ctx, output, time, freq, this.stepDuration * 2);
  }

  private schedulePadBar(ctx: AudioContext, bar: number, time: number): void {
    const pad = this.song.pad;
    const chord = pad[bar % pad.length];
    const freqs = chord.map((d) => noteFreq(scaleNote(d, 3)));
    const output = this.engine.getLayer('pad').gain;
    playPad(ctx, output, time, freqs, this.beatDuration * 4);
  }

  private scheduleArpStep(ctx: AudioContext, bar: number, step: number, time: number): void {
    const arp = this.song.arp;
    const pattern = arp[bar % arp.length];
    const degree = pattern[step % pattern.length];
    if (degree === -1) return;

    const freq = noteFreq(scaleNote(degree, 4));
    const output = this.engine.getLayer('arp').gain;
    playArp(ctx, output, time, freq, this.stepDuration * 0.8);
  }
}
